import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useT } from "@/i18n";
import { fetchDemand, type DemandSeries } from "@/services/production";

// "Kits asked for per month, over the last six months" — the only screen here that looks backwards.
//
// ⛔ EACH KIT IN ITS OWN COLOUR (his `demandColor`), the colour it has on every other card of the
// app, in his kit order, nothing folded away. Some of his colours are close (his three MUSA indigos),
// so the legend names every kit with its total and the hover names the segment.
//
// ⛔ THE TABLE IS NOT A NICETY. Several kit colours sit under 3:1 against white, so the chart
// carries its own exact-numbers view; colour is never the only way to read this.

const W = 720, H = 240, PAD_L = 40, PAD_B = 28, PAD_T = 12, PAD_R = 8;
const GAP = 2; // the surface shows between stacked segments

const colourOf = (s: DemandSeries) => s.colour;

// ⛔ ONLY THE DATA-END IS ROUNDED. `rx` on every segment turns a thin band into a floating pill, and
// a bar made of pills no longer reads as one stacked total — it reads as unrelated marks. Only the
// topmost segment of each bar gets rounded top corners; everything below it is square, so the
// segments stack into a single column.
function segment(x: number, y: number, w: number, h: number, roundTop: boolean) {
  const r = roundTop ? Math.min(4, h, w / 2) : 0;
  return `M${x},${y + h} V${y + r} ${r ? `a${r},${r} 0 0 1 ${r},-${r}` : ""} ` +
         `H${x + w - r} ${r ? `a${r},${r} 0 0 1 ${r},${r}` : ""} V${y + h} Z`;
}

export default function DemandChart({ warehouseId }: { warehouseId: number | null }) {
  const { t } = useT();
  const [view, setView] = useState<"chart" | "table">("chart");
  const [hover, setHover] = useState<{ m: number; s: number } | null>(null);
  const q = useQuery({ queryKey: ["demand", warehouseId], queryFn: () => fetchDemand(warehouseId) });

  const d = q.data;
  const max = useMemo(() => {
    const top = Math.max(1, ...(d?.totals ?? [0]));
    const step = top <= 10 ? 2 : top <= 50 ? 10 : top <= 200 ? 50 : 100;
    return Math.ceil(top / step) * step;
  }, [d]);

  if (q.isPending) return <p className="text-sm text-slate-400">{t("loading")}</p>;
  if (q.isError) return <p className="text-sm text-red-700">{(q.error as Error).message}</p>;
  if (!d) return null;

  const plotW = W - PAD_L - PAD_R, plotH = H - PAD_T - PAD_B;
  const band = plotW / d.months.length;
  const barW = Math.min(56, band * 0.62);
  const y = (v: number) => PAD_T + plotH - (v / max) * plotH;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));
  const empty = d.totals.every((n) => n === 0);

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4">
      <header className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-sm font-semibold text-slate-900">{t("demand.title")}</h2>
        <p className="text-xs text-slate-500">{t("demand.hint")}</p>
        <div className="ml-auto flex gap-1">
          {(["chart", "table"] as const).map((v) => (
            <button key={v} onClick={() => setView(v)}
              className={`rounded-lg px-2.5 py-1 text-xs transition ${
                view === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {t(v === "chart" ? "demand.chart" : "demand.table")}
            </button>
          ))}
        </div>
      </header>

      {empty && <p className="py-6 text-center text-sm text-slate-400">{t("demand.empty")}</p>}

      {!empty && view === "chart" && (
        <div className="relative">
          <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label={t("demand.title")}>
            {ticks.map((v) => (
              <g key={v}>
                <line x1={PAD_L} x2={W - PAD_R} y1={y(v)} y2={y(v)} className="stroke-slate-200" strokeWidth="1" />
                <text x={PAD_L - 6} y={y(v) + 4} textAnchor="end" className="fill-slate-400" fontSize="10">{v}</text>
              </g>
            ))}
            {d.months.map((m, mi) => {
              const x = PAD_L + band * mi + (band - barW) / 2;
              let acc = 0;
              return (
                <g key={m.key}>
                  {d.series.map((s, si) => {
                    const v = s.by_month[mi];
                    if (!v) return null;
                    const y0 = y(acc), y1 = y(acc + v);
                    acc += v;
                    // The gap eats into the segment, never past it: a tiny value stays visible.
                    const h = Math.max(2, y0 - y1 - GAP);
                    const on = hover?.m === mi && hover?.s === si;
                    const top = acc === d.totals[mi];
                    return (
                      <path key={s.code} d={segment(x, y1, barW, h, top)}
                        fill={colourOf(s)} opacity={hover && !on ? 0.45 : 1}
                        onMouseEnter={() => setHover({ m: mi, s: si })}
                        onMouseLeave={() => setHover(null)}>
                        <title>{t("demand.segment", { k: s.label || s.code, m: m.label, n: v, total: d.totals[mi] })}</title>
                      </path>
                    );
                  })}
                  <text x={x + barW / 2} y={H - PAD_B + 14} textAnchor="middle"
                    className="fill-slate-500" fontSize="10">{m.label}</text>
                  <text x={x + barW / 2} y={y(d.totals[mi]) - 5} textAnchor="middle"
                    className="fill-slate-700" fontSize="11" fontWeight="600">{d.totals[mi] || ""}</text>
                </g>
              );
            })}
          </svg>

          {hover && (
            <p className="mt-1 text-xs text-slate-700">
              <span className="mr-1.5 inline-block h-2.5 w-2.5 rounded-sm align-middle"
                style={{ background: colourOf(d.series[hover.s]) }} />
              <b>{d.series[hover.s].label || d.series[hover.s].code}</b> · {d.months[hover.m].label} ·{" "}
              {d.series[hover.s].by_month[hover.m]} {t("demand.kits")} {t("demand.ofMonth", { n: d.totals[hover.m] })}
            </p>
          )}
        </div>
      )}

      {!empty && view === "table" && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-500">
                <th className="py-1 text-left font-medium">{t("demand.kit")}</th>
                {d.months.map((m) => <th key={m.key} className="py-1 text-right font-medium">{m.label}</th>)}
                <th className="py-1 text-right font-medium">{t("demand.total")}</th>
              </tr>
            </thead>
            <tbody>
              {d.series.map((s) => (
                <tr key={s.code} className="border-t border-slate-100">
                  <td className="py-1">
                    <span className="mr-1.5 inline-block h-2.5 w-2.5 rounded-sm align-middle"
                      style={{ background: colourOf(s) }} />
                    {s.label || s.code}
                  </td>
                  {s.by_month.map((v, i) => (
                    <td key={i} className="py-1 text-right tabular-nums text-slate-700">{v || "·"}</td>
                  ))}
                  <td className="py-1 text-right font-semibold tabular-nums text-slate-900">{s.total}</td>
                </tr>
              ))}
              <tr className="border-t border-slate-300 font-semibold text-slate-900">
                <td className="py-1">{t("demand.total")}</td>
                {d.totals.map((v, i) => <td key={i} className="py-1 text-right tabular-nums">{v}</td>)}
                <td className="py-1 text-right tabular-nums">{d.totals.reduce((a, b) => a + b, 0)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {!empty && (
        <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
          {d.series.map((s) => (
            <li key={s.code} className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: colourOf(s) }} />
              {s.label || s.code}
              {/* His legend chip carries the kit's six-month total. */}
              <span className="tabular-nums text-slate-400">{s.total}</span>
            </li>
          ))}
        </ul>
      )}

      {d.never_requested.length > 0 && (
        <p className="mt-2 text-xs text-slate-500">
          {t("demand.never")}: {d.never_requested.map((k) => k.label || k.code).join(", ")}
        </p>
      )}
    </section>
  );
}
